import type { WorldTheme } from './theme';

/**
 * World 1 · MEDELLÍN theme (GDD §10.3, §14.2, issue #68). The dawn palette of OP-101, with the Aburrá
 * valley, brick houses and the cordillera in three parallax layers. Game art, not design system tokens
 * (GDD §14.1, §17-3); the definite values are recorded in GDD §14.2. Variants `mediodia` (OP-102, #98)
 * and `noche` (OP-103, #97) extend this structure.
 *
 * Legibility (GDD §14.1): the collidable tints (columns and pickups) are more saturated than every
 * decoration colour of `layer.mid` and `layer.far`, so nothing that hurts ever looks like scenery.
 */

const DECORATION = {
  /** Cordillera, far and desaturated. */
  cordillera: 0x8c7a8f,
  cordilleraSoft: 0x9a88a0,
  /** Mountains and brick houses of the mid layer. */
  hill: 0x6b8f55,
  hillDark: 0x54743f,
  brick: 0xa8785a,
  brickDark: 0x8a6248,
  /** Foreground band, below the ground line, darker than the play plane. */
  foreground: 0x3d5a30,
} as const;

export const WORLD_1_DAWN: WorldTheme = {
  id: 'world-1',
  variant: 'amanecer',
  sky: 0xf2a25c,
  fog: { color: 0xe89a6a, near: 40, far: 150 },
  ground: 0x5a8a44,
  ceiling: 0xd8d2e0,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    hemisphere: { sky: 0xffd9a8, ground: 0x4a5a3a, intensity: 1.0 },
    sun: { color: 0xffe6c2, intensity: 1.9, position: [-6, 14, 12] },
  },
  // Far to near: the cordillera, then the valley with its brick houses, then the grass band below the ground.
  layers: [
    {
      name: 'layer.far',
      colors: [DECORATION.cordillera, DECORATION.cordilleraSoft],
      moduleWidth: 26,
      height: 9,
      baseY: 1,
      motif: 'hills',
    },
    {
      name: 'layer.mid',
      colors: [DECORATION.hill, DECORATION.hillDark, DECORATION.brick, DECORATION.brickDark],
      moduleWidth: 16,
      height: 5,
      baseY: 1,
      motif: 'buildings',
    },
    {
      name: 'layer.foreground',
      colors: [DECORATION.foreground],
      moduleWidth: 10,
      height: 1.4,
      baseY: -0.6,
      motif: 'foreground',
      followCamera: true,
    },
  ],
};

/**
 * Night variant of world 1 (OP-103, issue #97, GDD §10.3): a purple Medellín. The decoration drops to a
 * cold, low-saturation purple-blue so the collidable tints and the skins stay unmistakable (GDD §14.1).
 */
const NIGHT_DECORATION = {
  cordillera: 0x3b3560,
  cordilleraSoft: 0x484070,
  hill: 0x2f4a55,
  hillDark: 0x253b45,
  brick: 0x5a4652,
  brickDark: 0x46363f,
  foreground: 0x1f2a2e,
  /** Warm window lights, the only warm accent of the night skyline. */
  lamp: 0xd9a520,
} as const;

export const WORLD_1_NIGHT: WorldTheme = {
  id: 'world-1',
  variant: 'noche',
  sky: 0x2a2350,
  fog: { color: 0x241d40, near: 40, far: 150 },
  ground: 0x2f4a30,
  ceiling: 0x9a94b0,
  collidable: { column: 0x1f8a2e, pickup: 0xe0a000 },
  lights: {
    // Cold moonlight; the hemisphere keeps a soft purple ambient, the sun is the moon.
    hemisphere: { sky: 0x6a6ab0, ground: 0x1a2030, intensity: 0.7 },
    sun: { color: 0xbfc8ff, intensity: 1.2, position: [-4, 16, 10] },
  },
  layers: [
    {
      name: 'layer.far',
      colors: [NIGHT_DECORATION.cordillera, NIGHT_DECORATION.cordilleraSoft],
      moduleWidth: 26,
      height: 9,
      baseY: 1,
      motif: 'hills',
    },
    {
      name: 'layer.mid',
      colors: [NIGHT_DECORATION.hill, NIGHT_DECORATION.hillDark, NIGHT_DECORATION.brick, NIGHT_DECORATION.brickDark],
      moduleWidth: 16,
      height: 5,
      baseY: 1,
      motif: 'buildings',
    },
    {
      name: 'layer.foreground',
      colors: [NIGHT_DECORATION.foreground],
      moduleWidth: 10,
      height: 1.4,
      baseY: -0.6,
      motif: 'foreground',
      followCamera: true,
    },
  ],
};

export const WORLD_1: Readonly<Record<string, WorldTheme>> = {
  amanecer: WORLD_1_DAWN,
  noche: WORLD_1_NIGHT,
};
